import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/auth/AuthContext";
import { inputCls, labelCls } from "@/components/formkit";

export default function ConcealmentRequest({ prefill, bookingId }) {
  const [mode, setMode] = useState("photos");
  const [form, setForm] = useState({
    name: prefill?.name || "", phone: prefill?.phone || "", email: prefill?.email || "",
    address: prefill?.address || "", preferred_date: "", preferred_time: "", description: "",
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, mode, booking_id: bookingId || "" }).forEach(([k, v]) => fd.append(k, v));
      photos.slice(0, 6).forEach((p) => fd.append("photos", p));
      await axios.post(`${API}/applications/concealment`, fd);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed — please call 469-897-6497.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="concealment-success">
        <p className="text-sm text-emerald-400 font-medium">Concealment request received.</p>
        <p className="text-[11px] text-zinc-400 font-mono mt-2 leading-relaxed">
          DuroTech28 will review your project and send a custom estimate. No work begins and nothing
          is charged until you approve the final estimate.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 rounded-xl bg-[#0d0d10] border border-[#d4af37]/30 p-5 text-left" data-testid="concealment-form">
      <h3 className="font-display text-lg uppercase text-white">Custom In-Wall Concealment</h3>
      <p className="text-[10px] font-mono text-[#d4af37] mt-1 mb-4">Starting at $350 — Final Quote Required</p>

      <div className="grid grid-cols-2 gap-2 mb-5">
        {[["photos", "Request Quote Using Photos"], ["onsite", "Request On-Site Assessment"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => setMode(v)} data-testid={`concealment-mode-${v}`}
            className={`px-3 py-2.5 rounded border text-[11px] font-mono transition-colors ${
              mode === v ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]" : "bg-[#0a0a0c] border-zinc-700 text-zinc-400"}`}>
            {l}
          </button>
        ))}
      </div>

      {mode === "photos" && (
        <p className="text-[11px] text-zinc-400 font-mono leading-relaxed mb-4 border-l-2 border-[#d4af37]/40 pl-3">
          Please upload clear pictures showing where you want your TV installed and the location of the
          nearest electrical outlet — the entire wall, the distance between the outlet and the proposed TV
          location, and any obstacles or special conditions. These help us evaluate the installation and
          provide a preliminary estimate. Additional inspection may be required before final pricing is confirmed.
        </p>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={labelCls}>Name *</label>
          <input required value={form.name} onChange={set("name")} data-testid="conc-name" className={inputCls} /></div>
        <div><label className={labelCls}>Phone *</label>
          <input required value={form.phone} onChange={set("phone")} data-testid="conc-phone" className={inputCls} /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Email *</label>
          <input required type="email" value={form.email} onChange={set("email")} data-testid="conc-email" className={inputCls} /></div>
        <div className="sm:col-span-2"><label className={labelCls}>Service Address</label>
          <input value={form.address} onChange={set("address")} data-testid="conc-address" className={inputCls} /></div>
        {mode === "onsite" && (
          <>
            <div><label className={labelCls}>Preferred Assessment Date</label>
              <input type="date" value={form.preferred_date} onChange={set("preferred_date")} data-testid="conc-date" className={inputCls} /></div>
            <div><label className={labelCls}>Preferred Time</label>
              <input type="time" value={form.preferred_time} onChange={set("preferred_time")} data-testid="conc-time" className={inputCls} /></div>
          </>
        )}
      </div>

      {mode === "photos" && (
        <div className="mt-3">
          <span className={labelCls}>Project photos (up to 6)</span>
          <label className="flex items-center justify-center w-full border border-dashed border-zinc-600 hover:border-[#d4af37] rounded p-4 text-[11px] font-mono text-zinc-400 cursor-pointer transition-colors" data-testid="conc-photos">
            <input type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => setPhotos(Array.from(e.target.files || []))} />
            {photos.length ? <span className="text-[#d4af37]">{photos.length} photo(s) attached</span> : "Tap to attach wall / outlet / room photos"}
          </label>
        </div>
      )}

      <div className="mt-3"><label className={labelCls}>Project description (optional)</label>
        <textarea rows="3" value={form.description} onChange={set("description")} data-testid="conc-description" className={inputCls}></textarea></div>

      {mode === "onsite" && (
        <p className="text-[10px] text-zinc-500 font-mono mt-3">Assessment appointments are reviewed and approved by DuroTech28 before confirmation.</p>
      )}

      <button type="submit" disabled={submitting} data-testid="conc-submit"
        className="mt-4 w-full py-3.5 rounded bg-gradient-to-r from-[#d4af37] to-[#e6ca65] text-black font-bold uppercase tracking-wider text-xs hover:opacity-95 transition-opacity disabled:opacity-60">
        {submitting ? "Submitting..." : mode === "photos" ? "Request Photo Quote" : "Request On-Site Assessment"}
      </button>
    </form>
  );
}
